/**
 * 社外依頼(協力会社などへのメール)。送信・状態の前進・送信記録。
 *
 * 一覧テンプレート。ビューは状態(未依頼 → 依頼済 → 先方完了報告 → 自社確認済)。
 * 「文面」と行クリックで右パネル(`?panel=<id>`)。パネルは ID で 1 件取得する(一覧のページに依らない)。
 */
import { useMutation, useQueries } from "@tanstack/react-query";
import { Alert, Button, Space, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { WfExternalRequest } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  DataTable,
  FilterBar,
  PageHeader,
  PARTIAL_CLEAR_LABEL,
  PartialResultNote,
  partialResultText,
  TimeAgo,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type DataColumn,
} from "../../ui";
import { ExternalRequestPanel } from "./ExternalRequestPanel";
import { EXTERNAL_FLOW } from "./constants";
import { errorText } from "./hooks";
import { employeeLink, externalStatusTag, matchesAll, requestLink } from "./wfBits";
import { useExternalRequests, useInvalidateWf, useWfPerms } from "./wfHooks";

const LOCAL_LIMIT = "200";
const VIEWS = [{ value: "all", label: "すべて" }, ...EXTERNAL_FLOW];

function useStatusCounts() {
  const api = useApi();
  const results = useQueries({
    queries: VIEWS.map((v) => ({
      queryKey: ["hr", "wf-external", "count", v.value],
      queryFn: () =>
        api.externalRequests(new URLSearchParams({ limit: "1", offset: "0", ...(v.value === "all" ? {} : { status: v.value }) })),
      select: (d: { total: number }) => d.total,
      staleTime: 60_000,
    })),
  });
  return Object.fromEntries(VIEWS.map((v, i) => [v.value, results[i].data]));
}

export function ExternalRequestsPage() {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const { canOperate } = useWfPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const status = VIEWS.some((v) => v.value === sp.get("status")) ? (sp.get("status") as string) : "all";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const local = Boolean(q);
  const filters: Record<string, string> = status === "all" ? {} : { status };
  const query = useExternalRequests(
    local
      ? { ...filters, limit: LOCAL_LIMIT, offset: "0" }
      : { ...filters, limit: String(pageSize), offset: String((page - 1) * pageSize) },
  );
  const counts = useStatusCounts();
  useEffect(() => setPage(1), [q, status]);

  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    if (!local) return items;
    return items.filter((e) =>
      matchesAll(q, e.id, e.employee_name, e.partner_name, e.partner_email, e.action_label, e.items.join(" "), e.subject),
    );
  }, [query.data, local, q]);
  const total = local ? rows.length : (query.data?.total ?? 0);
  // 手元で絞っていて、取得が上限で切れている(これより古い依頼は対象外。fix1 中4)
  const fetched = query.data?.items.length ?? 0;
  const partial = local && (query.data?.total ?? 0) > Number(LOCAL_LIMIT);
  const clearLocal = () => {
    setQInput("");
    patch({ q: null }, true);
  };

  const send = useMutation({
    mutationFn: (id: number) => api.sendExternalRequest(id),
    onSuccess: (res) => {
      void message.success(res.sent ? `送信した(${res.to})` : `送らずに記録した: ${res.message ?? ""}`);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: DataColumn<WfExternalRequest>[] = [
    { key: "id", title: "#", ellipsis: false, dataIndex: "id", width: 110, render: (v: number) => <span className="ui-num">{v}</span> },
    {
      key: "employee",
      title: panelId ? "対象者" : "申請・対象者",
      ellipsis: false,
      width: 200,
      render: (_: unknown, e) => panelId ? (
        <span className="ui-cell-main" title={e.employee_name}>
          {employeeLink(e.employee_id, e.employee_name)}
        </span>
      ) : (
        <div>
          <div>{requestLink(e.request_id, e.request_kind)}</div>
          <div className="ui-cell-sub">{employeeLink(e.employee_id, e.employee_name)}</div>
        </div>
      ),
    },
    {
      key: "partner",
      title: "依頼先",
      ellipsis: false,
      width: 240,
      render: (_: unknown, e) => (
        <div>
          <div className="ui-cell-main" title={e.partner_name}>{e.partner_name}</div>
          <div className="ui-cell-sub" title={e.partner_email || "宛先なし"}>{e.partner_email || "宛先なし"}</div>
        </div>
      ),
    },
    { key: "action", title: "向き", ellipsis: false, dataIndex: "action_label", width: 160 },
    {
      key: "items",
      title: "依頼項目",
      width: 320,
      className: "ui-external-request-items",
      ellipsis: false,
      render: (_: unknown, e) => (
        <span className="ui-cell-main" title={e.items.join(" / ")}>
          {e.items.join(" / ")}
        </span>
      ),
    },
    {
      key: "status",
      title: "状態",
      width: 200,
      className: panelId ? "ui-external-request-status" : undefined,
      ellipsis: false,
      render: (_: unknown, e) => externalStatusTag(e),
    },
    { key: "sent", title: "送信", ellipsis: false, width: 160, render: (_: unknown, e) => (e.sent_at ? <TimeAgo iso={e.sent_at} /> : "") },
    {
      key: "operations",
      title: "操作",
      ellipsis: false,
      width: 180,
      render: (_: unknown, e) => (
        <Space size={4} wrap>
          <Button size="small" onClick={() => setPanelId(String(e.id))}>
            文面
          </Button>
          {canOperate && (e.status === "draft" || e.send_unknown) && (
            <Button size="small" type="primary" loading={send.isPending} onClick={() => send.mutate(e.id)}>
              {e.send_unknown ? "再送" : "送信"}
            </Button>
          )}
        </Space>
      ),
    },
  ];
  // 文面パネルを開いたときは4列・計396px。氏名を優先し、状態タグは折り返す。
  const visibleColumns = panelId
    ? columns.filter((column) => ["employee", "partner", "status", "operations"].includes(String(column.key)))
    : columns;

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="社外依頼"
          description="承認が揃うと、依頼先と向きごとに 1 通ずつ下書きが作られる。送信すると完了報告URL付きのメールを出し、先方はログイン無しで報告できる(URLは 1 回で使い切り・期限つき)。SMTP が未設定のときは送らずに送信記録だけ残す。"
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "依頼先 / 宛先 / 対象者 / 件名" }}
          views={{
            items: VIEWS.map((v) => ({ key: v.value, label: v.label, count: counts[v.value] })),
            active: status,
            onChange: (key) => patch({ status: key === "all" ? null : key }, true),
          }}
          right={
            q ? (
              <Button size="small" onClick={clearLocal}>
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="社外依頼を取得できなかった" description={errorText(query.error)} />
        )}
        {partial && <PartialResultNote limit={Number(LOCAL_LIMIT)} onClear={clearLocal} />}
        <DataTable<WfExternalRequest>
          columns={visibleColumns}
          scrollX={visibleColumns.reduce((sum, column) => sum + column.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={total}
          countLabel={partial ? `${fetched} 件中 ${rows.length} 件` : undefined}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          selectedKey={panelId ? Number(panelId) : null}
          onRowClick={(row) => setPanelId(String(row.id))}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: `${q ? "条件に合う社外依頼はない" : status === "all" ? "社外依頼はない(承認が揃うと下書きができる)" : "この状態の社外依頼はない"}${
              partial ? `(${partialResultText(Number(LOCAL_LIMIT))})` : ""
            }`,
            action: partial ? <Button onClick={clearLocal}>{PARTIAL_CLEAR_LABEL}</Button> : undefined,
          }}
        />
      </div>
      {panelId && (
        // 依頼ごとに別のパネル(編集中の本文・送信中の操作を持ち越さない。fix1 中1)
        <ExternalRequestPanel key={panelId} id={Number(panelId)} onClose={() => setPanelId(null)} />
      )}
    </div>
  );
}
